import { Button } from "../ui/button";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { addLanguagePrefix, type SupportedLanguage } from "@/lib/i18n";

// Original fallback
import heroBackground from "@/assets/hero-background-new.jpg";
// WebP responsive variants for better performance
import heroWebpSmall from "@/assets/hero-background-new.jpg?w=800&format=webp";
import heroWebpMedium from "@/assets/hero-background-new.jpg?w=1200&format=webp";
import heroWebpLarge from "@/assets/hero-background-new.jpg?w=1920&format=webp";

const HeroSection = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('home');
  const { t: tCommon } = useTranslation('common');
  const currentLang = i18n.language as SupportedLanguage;

  const navigateTo = (path: string) => {
    const langPath = addLanguagePrefix(path, currentLang);
    navigate(langPath);
  };

  // Build srcset for responsive images
  const heroSrcSet = `${heroWebpSmall} 800w, ${heroWebpMedium} 1200w, ${heroWebpLarge} 1920w`;

  return (
    <>
      <Helmet>
        {/* Preload the LCP image with high priority */}
        <link
          rel="preload"
          as="image"
          href={heroWebpLarge}
          imageSrcSet={heroSrcSet}
          imageSizes="100vw"
          type="image/webp"
        />
      </Helmet>
      <section className="relative w-full min-h-[90vh] flex items-center overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <picture>
            <source
              type="image/webp"
              srcSet={heroSrcSet}
              sizes="100vw"
            />
            <img
              src={heroBackground}
              alt="Altum Contractors Houston roofing team installing a new residential roof"
              className="absolute inset-0 w-full h-full object-cover object-center scale-105"
              fetchPriority="high"
              decoding="async"
            />
          </picture>
          {/* Gradient overlays - Navy blue tint */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#001F3F]/95 via-[#001F3F]/60 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#001F3F]/85 via-transparent to-transparent" />
          {/* Subtle texture overlay */}
          <div className="absolute inset-0 opacity-10 bg-[url('data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.1\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] mix-blend-overlay" />
        </div>

        {/* Content Container */}
        <div className="relative z-10 mx-auto max-w-7xl px-4 pt-32 pb-20 sm:px-6 lg:px-8 w-full">
          <div className="max-w-4xl">
            {/* Main Headline - Stacked Bold Style */}
            <motion.h1
              className="font-heading font-extrabold leading-[0.95] tracking-tight text-white text-5xl sm:text-6xl lg:text-7xl xl:text-8xl drop-shadow-2xl mb-8"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
            >
              {t('hero.headlinePart1')} <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-white via-slate-200 to-slate-400">
                {t('hero.headlinePart2')}
              </span>
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-red-600">
                {t('hero.headlinePart3')}
              </span>
            </motion.h1>

            {/* Subheadline with border accent */}
            <motion.p
              className="text-xl sm:text-2xl font-medium text-slate-200/90 max-w-2xl leading-relaxed border-l-4 border-primary pl-6 mb-10"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              {t('hero.subheadline')}
            </motion.p>

            {/* CTA Button */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
            >
              <Button
                variant="cta"
                onClick={() => {
                  if (typeof window !== 'undefined' && (window as any).fbq) {
                    (window as any).fbq('track', 'Contact', {
                      content_name: 'Get Free Inspection Button - Hero'
                    });
                  }
                  navigateTo("/book-inspection");
                }}
                className="text-lg font-bold px-10 py-5 rounded-full shadow-xl shadow-red-900/30 hover:shadow-red-900/50 transform hover:-translate-y-1 hover:scale-105 transition-all"
              >
                {tCommon('cta.getFreeInspection')}
              </Button>
            </motion.div>

            {/* Micro-copy */}
            <motion.p
              className="text-white/60 text-sm mt-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.8 }}
            >
              {t('hero.microcopy')}
            </motion.p>
          </div>
        </div>
      </section>
    </>
  );
};

export default HeroSection;
